import React, { useState } from 'react';
import AppLayout from '../components/AppLayout';
import { patientApi } from '../services/patientApi';
import { getKnownDepartments } from '../utils/patientViewModel';
import { useNavigate } from 'react-router-dom';

const knownDepartments = getKnownDepartments();

const PatientForm = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    historia_clinica: '',
    edad: '',
    genero: 'masculino',
    diagnostico: '',
    habitacion: '',
    fecha_ingreso: '',
    estado: 'hospitalizado',
    department: '',
  });

  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const navigate = useNavigate();

  const setField = (key) => (event) => {
    setForm((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const enviar = async (event) => {
    event.preventDefault();

    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.historia_clinica.trim()
    ) {
      setError(
        'El nombre, apellido y número de expediente son obligatorios.'
      );
      return;
    }

    setGuardando(true);
    setError('');

    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        medicalRecordNumber: form.historia_clinica.trim(),
        birthDate: null,
        notes: form.diagnostico.trim() || null,
        habitacion: form.habitacion.trim() || null,
        department: form.department || null,
      };

      console.log('Enviando paciente:', payload);

      const response = await patientApi.createPatient(payload);

      console.log('Paciente creado:', response.data);

      navigate('/');
    } catch (err) {
      console.error('Error creando paciente:', err);
      console.error('Respuesta:', err?.response?.data);

      setError(
        err?.response?.status === 400
          ? 'Datos inválidos o expediente duplicado.'
          : 'No se pudo guardar el paciente.'
      );
    } finally {
      setGuardando(false);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Nuevo Paciente
            </h1>
          </div>
        </div>

        <form
          onSubmit={enviar}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-900">
            Registrar paciente
          </h2>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">

            {/* Nombre */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              placeholder="Nombre"
              value={form.firstName}
              onChange={setField('firstName')}
            />

            {/* Apellido */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              placeholder="Apellido"
              value={form.lastName}
              onChange={setField('lastName')}
            />

            {/* Expediente */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              placeholder="N.º expediente"
              value={form.historia_clinica}
              onChange={setField('historia_clinica')}
            />

            {/* Edad */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              type="number"
              min="0"
              max="120"
              placeholder="Edad"
              value={form.edad}
              onChange={setField('edad')}
            />

            {/* Género */}
            <select
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              value={form.genero}
              onChange={setField('genero')}
            >
              <option value="masculino">Masculino</option>
              <option value="femenino">Femenino</option>
              <option value="otro">Otro</option>
            </select>

            {/* Habitación */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              placeholder="Habitación / cama"
              value={form.habitacion}
              onChange={setField('habitacion')}
            />

            {/* Fecha ingreso */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              type="date"
              value={form.fecha_ingreso}
              onChange={setField('fecha_ingreso')}
            />

            {/* Departamento */}
            <select
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 md:col-span-1"
              value={form.department}
              onChange={setField('department')}
            >
              <option value="">
                Departamento (opcional)
              </option>

              {knownDepartments.map((departmentName) => (
                <option
                  key={departmentName}
                  value={departmentName}
                >
                  {departmentName}
                </option>
              ))}

              <option value="Otros">Otros</option>
            </select>

            {/* Diagnóstico */}
            <input
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 md:col-span-2 xl:col-span-2"
              placeholder="Diagnóstico de ingreso"
              value={form.diagnostico}
              onChange={setField('diagnostico')}
            />
          </div>

          {error && (
            <p className="mt-3 text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={guardando}
              className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-70"
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default PatientForm;